import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';

import { ActivityItem as ActivityItemRow } from '../../components/activity/ActivityItem';
import { CoinIcon } from '../../components/CoinIcon';
import { Badge } from '../../components/ui';
import { useAppPath } from '../../lib/appBase';
import { formatUsd, formatUsdSigned, isRawMarketHash } from '../../lib/format';
import { sideLabel, strategyLabel } from '../../lib/strategyLabel';
import type {
  ActivityItem,
  HlBalanceResponse,
  PmMarketForMe,
  PositionItem,
} from '../../lib/types';
import { CabEmpty, Panel } from '../parts';

function Pnl({ value }: { value: string | number | null }) {
  const n = Number(value ?? 0);
  return (
    <span
      className={clsx(
        'pb-numeral font-semibold',
        n > 0 ? 'text-pb-success' : n < 0 ? 'text-pb-danger' : 'text-tg-hint',
      )}
    >
      {formatUsdSigned(value)}
    </span>
  );
}

function Empty({ msg }: { msg: string }) {
  return <CabEmpty msg={msg} />;
}

/* ── PM open positions ──────────────────────────────────────────── */

export function PmPositions({
  positions,
  markets,
}: {
  positions: PositionItem[];
  markets: PmMarketForMe[] | undefined;
}) {
  const { t } = useTranslation();
  const mMap = new Map((markets ?? []).map((m) => [m.market_id, m.display_name]));
  const rows = positions.filter((p) => p.bot_kind === 'polymarket');
  if (!rows.length) return <Empty msg={t('cabinet.no_positions')} />;
  const COLS = 'minmax(0,1.8fr) 0.7fr 0.9fr 0.9fr';
  return (
    <div>
      <div
        className="hidden gap-3 border-b border-pb-border-soft pb-2 sm:grid"
        style={{ gridTemplateColumns: COLS }}
      >
        {['col_market', 'col_side', 'col_token_price', 'col_amount'].map((k) => (
          <span key={k} className="pb-eyebrow">
            {t(`cabinet.${k}`)}
          </span>
        ))}
      </div>
      <div className="divide-y divide-pb-border-soft">
        {rows.map((p) => {
          const name =
            p.market_id && mMap.has(p.market_id)
              ? mMap.get(p.market_id)
              : isRawMarketHash(p.market)
                ? strategyLabel(p.strategy, null, t)
                : p.market;
          const cents = Math.round(Number(p.entry_price) * 100);
          return (
            <div
              key={p.id}
              className="grid items-center gap-2 py-3 sm:gap-3"
              style={{ gridTemplateColumns: COLS }}
            >
              <div className="min-w-0">
                <div className="truncate text-[14px] font-medium text-tg-text">{name}</div>
              </div>
              <div className="text-[13px] text-tg-text">{sideLabel(p.side, t)}</div>
              <div className="pb-numeral text-[13px] font-medium text-tg-text">
                {Number.isFinite(cents) ? `${cents}¢` : '—'}
              </div>
              <div className="pb-numeral text-[13px] text-tg-text">
                {formatUsd(p.notional_usdc)}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}


/* ── HL open positions (live mark + unrealized) ─────────────────── */

export function HlPositions({
  positions,
  hl,
}: {
  positions: PositionItem[];
  hl: HlBalanceResponse | undefined;
}) {
  const { t } = useTranslation();
  const hlByCoin = new Map((hl?.positions ?? []).map((p) => [p.coin, p]));
  const rows = positions.filter((p) => p.bot_kind === 'hyperliquid');
  if (!rows.length) return <Empty msg={t('cabinet.no_positions')} />;
  return (
    <div className="grid gap-3 sm:grid-cols-2">
      {rows.map((p) => {
        const live = hlByCoin.get(p.market);
        const long = p.side === 'LONG' || p.side === 'BUY';
        const pnlVal = live?.unrealized_pnl ?? null;
        return (
          <div
            key={p.id}
            className="rounded-pb-md border border-pb-border-soft bg-pb-paper2/50 p-3.5 transition-all duration-200 hover:-translate-y-0.5 hover:border-pb-accent-line hover:shadow-pb-md"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <CoinIcon symbol={p.market} size={22} />
                <span className="text-[15px] font-semibold text-tg-text">{p.market}</span>
                <Badge tone={long ? 'success' : 'danger'}>{sideLabel(p.side, t)}</Badge>
                <span className="pb-numeral text-[12px] text-tg-hint">{p.leverage}×</span>
              </div>
              <div className="text-right text-[15px]">
                {pnlVal != null ? <Pnl value={pnlVal} /> : <span className="text-tg-hint">—</span>}
              </div>
            </div>
            <div className="mt-3 grid grid-cols-3 gap-2">
              <Stat label={t('cabinet.lbl_entry')} value={Number(p.entry_price).toFixed(1)} />
              <Stat
                label={t('cabinet.lbl_mark')}
                value={live ? Number(live.mark_price).toFixed(1) : '—'}
              />
              <Stat
                label={t('cabinet.lbl_amount')}
                value={formatUsd(live?.position_value ?? p.notional_usdc)}
              />
              <Stat
                label={t('cabinet.lbl_tp')}
                value={p.take_profit ? Number(p.take_profit).toFixed(1) : '—'}
                tone="up"
              />
              <Stat
                label={t('cabinet.lbl_sl')}
                value={p.stop_loss ? Number(p.stop_loss).toFixed(1) : '—'}
                tone="down"
              />
              <Stat label={t('cabinet.lbl_size')} value={Number(p.size).toString()} />
            </div>
          </div>
        );
      })}
    </div>
  );
}

function Stat({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone?: 'up' | 'down';
}) {
  return (
    <div className="rounded-pb-sm bg-pb-paper/60 px-2.5 py-1.5">
      <div className="pb-eyebrow text-[9px]">{label}</div>
      <div
        className={clsx(
          'pb-numeral text-[13px] font-semibold',
          tone === 'up'
            ? 'text-pb-success'
            : tone === 'down'
              ? 'text-pb-danger'
              : 'text-tg-text',
        )}
      >
        {value}
      </div>
    </div>
  );
}

/* ── Recent activity (links to the Activity section) ────────────── */

export function RecentActivity({
  items,
  lang,
}: {
  items: ActivityItem[];
  lang: string;
}) {
  const { t } = useTranslation();
  const ap = useAppPath();
  const recent = items.slice(0, 8);
  const locale = lang.startsWith('ru') ? 'ru-RU' : 'en-US';
  return (
    <Panel
      title={t('cabinet.recent_trades')}
      action={
        <Link
          to={ap('/activity')}
          className="pb-focus-ring inline-flex items-center gap-1 rounded-pb-sm px-2 py-1 text-[12px] font-medium text-pb-accent hover:bg-pb-accent-soft"
        >
          {t('cabinet.all_activity')} →
        </Link>
      }
    >
      {!recent.length ? (
        <Empty msg={t('cabinet.no_activity')} />
      ) : (
        <div className="-mx-2 divide-y divide-pb-border-soft">
          {recent.map((it) => (
            <ActivityItemRow key={it.id} item={it} locale={locale} />
          ))}
        </div>
      )}
    </Panel>
  );
}
